import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { LoginModule, ClpService, ClpLoaderService } from '@reef/login';
import { LoginComponent } from './core/login/login.component';
import { HttpModule as SecoHttpModule, MockClpLoaderService } from '@reef/dev-utils';
import { AppComponent } from './app.component';
import { StoreModule } from '@ngrx/store';
import { ReefServerModule, AnalyticsService, HttpConfigService, ReefGatewayService } from '@reef/core';
import { ReefRESTService } from '@reef/fwk-utils';
import { ReefRESTServiceImpl } from './service/reef-rest.service';
import { CORE_FEATURE } from './store/core/state';
import { coreReducer } from './store/core/reducer';
import { POS_FEATURE } from './store/pos/pos-state';
import { posReducer } from './store/pos/pos-reducer';
import { MARKETS_FEATURE } from './store/markets/markets-state';
import { marketsReducer } from './store/markets/markets-reducer';
import { PosEffects } from './store/pos/pos-effects.service';
import { MarketsEffects } from './store/markets/markets-effects.service';
import { EffectsModule } from '@ngrx/effects';
import { LoginService } from './service/login.service';
import {
  HTTP_INTERCEPTORS,
  HttpClientModule,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { ReactiveFormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { PointsOfSaleModule } from './points-of-sale/points-of-sale.module';
import { MarketsModule } from './markets/markets.module';
import { HotelsModule } from './hotels/hotels.module';
import { HOTELS_FEATURE } from './store/hotel/hotels-state';
import { hotelsReducer } from './store/hotel/hotels-reducer';
import { HotelsEffects } from './store/hotel/hotels-effects.service';
import { FlightsModule } from './flights/flights.module';
import { FLIGHTS_FEATURE } from './store/flight/flights-state';
import { flightsReducer } from './store/flight/flights-reducer';
import { FlightsEffects } from './store/flight/flights-effects.service';
import { FamiliesModule } from './families/families.module';
import { FAMILIES_FEATURE } from './store/families/families-state';
import { familiesReducer } from './store/families/families-reducer';
import { FamiliesEffects } from './store/families/families-effects.service';
import { NAVIGATION_FEATURE } from './store/navigation/navigation-state';
import { navigationReducer } from './store/navigation/navigation-reducer';
import { environment } from '../environments/environment';
import { DfAlertModule } from '@design-factory/design-factory';
import { FaresModule } from './fares/fares.module';
import { FARES_FEATURE } from './store/fares/fares-state';
import { faresReducer } from './store/fares/fares-reducer';
import { FaresEffects } from './store/fares/fares-effects.service';
import { DummyAnalyticsService } from './service/dummy-analytics.service';
import { Observable, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { URLUtils } from './util/url-utils';
import { commonReducer } from './store/common/common-reducers';
import { COMMON_FEATURE } from './store/common/common-selectors';
import { AIR_FAMILIES_FEATURE } from './store/families/air/air-families-selector';
import { airFamiliesReducer } from './store/families/air/air-families-reducer';
import { AirFamilyEffects } from './store/families/air/air-families-effects.service';
import { UnauthorizedInterceptor } from './interceptors/unauthorized.interceptor';
import { AmaRequestIdInterceptor } from './interceptors/ama-request-id.interceptor';
import { CardDeckComponent } from './card-deck/card-deck.component';
import { CardComponent } from './card/card.component';
import { ConfirmationModalComponent } from './components/confirmation-modal/confirmation-modal.component';
import { AboutComponent } from './core/about/about.component';
import { FooterComponent } from './core/footer/footer.component';
import { HeaderComponent } from './core/header/header.component';
import { NavigationMenuComponent } from './core/navigation-menu/navigation-menu.component';
import { DashboardComponent } from './dashboard/dashboard.component';

export function initializeApp(
  loginService: LoginService,
  httpConfigService: HttpConfigService,
  gwService: ReefGatewayService
): () => Observable<any> {
  return () => {
    const siteCode = '0SLW0SLW';
    const language = 'GB';
    const baseUrl = '/app_upp_ui';

    httpConfigService.initConfig({
      siteCode,
      language,
      baseUrl,
      environment: URLUtils.getQueryParamValue(window.location.href, 'OCTX')
    });

    // Set version in gwService
    gwService.setVersion('v3');

    // Trigger a timout after 10 secs
    return loginService
      .getClpConfig()
      .pipe(timeout(AppModule.MAX_INIT_TIME))
      .pipe(
        catchError((err) => {
          console.log('Error on getClpConfig:', err);
          // Avoid to block application
          return of();
        })
      );
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    NgbModule,
    SecoHttpModule.forRoot(undefined, environment.useMockServer),
    StoreModule.forRoot({}),
    StoreModule.forFeature(CORE_FEATURE, coreReducer),
    StoreModule.forFeature(POS_FEATURE, posReducer),
    StoreModule.forFeature(MARKETS_FEATURE, marketsReducer),
    StoreModule.forFeature(HOTELS_FEATURE, hotelsReducer),
    StoreModule.forFeature(FLIGHTS_FEATURE, flightsReducer),
    StoreModule.forFeature(FAMILIES_FEATURE, familiesReducer),
    StoreModule.forFeature(FARES_FEATURE, faresReducer),
    StoreModule.forFeature(COMMON_FEATURE, commonReducer),
    StoreModule.forFeature(AIR_FAMILIES_FEATURE, airFamiliesReducer),
    EffectsModule.forFeature([
      PosEffects,
      MarketsEffects,
      HotelsEffects,
      FlightsEffects,
      AirFamilyEffects,
      FamiliesEffects,
      FaresEffects
    ]),
    StoreModule.forFeature(NAVIGATION_FEATURE, navigationReducer),
    EffectsModule.forRoot([]),
    LoginModule,
    ReactiveFormsModule,
    PointsOfSaleModule,
    MarketsModule,
    HotelsModule,
    FlightsModule,
    FamiliesModule,
    FaresModule,
    ReefServerModule.forRoot(),
    AppRoutingModule,
    DfAlertModule,
    HeaderComponent,
    NavigationMenuComponent,
    DashboardComponent,
    AboutComponent,
    FooterComponent,
    CardComponent,
    CardDeckComponent,
    ConfirmationModalComponent,
    LoginComponent
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([AmaRequestIdInterceptor])),
    { provide: HTTP_INTERCEPTORS, useClass: UnauthorizedInterceptor, multi: true },
    { provide: AnalyticsService, useClass: DummyAnalyticsService },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [LoginService, HttpConfigService, ReefGatewayService],
      multi: true
    },
    LoginService,
    ClpService,
    { provide: ReefRESTService, useClass: ReefRESTServiceImpl },
    { provide: ClpLoaderService, useClass: environment.useMockServer ? MockClpLoaderService : ClpLoaderService }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  // Max time to startup app : 10 secs
  public static readonly MAX_INIT_TIME = 10_000;
}
